import { inject, Type } from '@angular/core';
import { GuardResult, MaybeAsync } from '@angular/router';
import { concatMap, from, isObservable, last, takeWhile } from 'rxjs';

import { Guards, GuardTypes, MaybeType, ToInstance, ToParams } from './types';

const assertMethod = <Method extends GuardTypes>(
  type: Method,
  method: GuardTypes,
  guard: Guards,
): guard is ToInstance<Method> => {
  // Since passed guards can implement methods for multiple
  // guard types, we need to ensure the type matches to method
  // to avoid multiple calls.
  return type === method && method in guard;
};

const isClassGuard = <GuardType extends GuardTypes>(
  arg: MaybeType<ToInstance<GuardType>>,
): arg is Type<ToInstance<GuardType>> => {
  // Classes have non-writable prototypes in contrast to
  // functions. Arrow functions do not have a prototype.
  // See also: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Function/prototype
  return Object.getOwnPropertyDescriptor(arg, 'prototype')?.writable === false;
};

export const queueGuards = <
  GuardType extends GuardTypes,
  Guard extends Guards = ToInstance<GuardType>,
>(
  /** An array of `CanActivate|CanActivateChild|CanMatch`
   * guard classes or guard factory functions.*/
  guards: NoInfer<Array<MaybeType<Guard>>>,
  /** Supply `canActivate|canActivateChild|canMatch`
   * argument to determine which guard method should be run. If omitted,
   * it defaults to 'canActivate' */
  type: GuardType = 'canActivate' as GuardType,
) => {
  // TS will not infer the tuple if we use the type helper here.
  return (...params: unknown[]) => {
    const injectedGuards = guards.map((guard) =>
      isClassGuard(guard) ? inject(guard) : guard(),
    );

    return from(injectedGuards).pipe(
      concatMap((guard) => {
        let result: MaybeAsync<GuardResult> = false;
        if (assertMethod('canActivate', type, guard)) {
          result = guard.canActivate(...(params as ToParams<'canActivate'>));
        } else if (assertMethod('canActivateChild', type, guard)) {
          result = guard.canActivateChild(
            ...(params as ToParams<'canActivateChild'>),
          );
        } else if (assertMethod('canMatch', type, guard)) {
          result = guard.canMatch(...(params as ToParams<'canMatch'>));
        }
        return isObservable(result) ? result : from(Promise.resolve(result));
      }),
      takeWhile((canPass) => canPass === true, true),
      last(),
    );
  };
};
